import { Post } from "@/src/models/post.model";
import { htmlToFormattedExcerpt } from "@/src/utils/rich-text.utils";

/**
 * A published news article is mirrored into the feed as an ordinary post, so it
 * scrolls, reacts, comments and shares exactly like one. The copy is deliberate
 * duplication: nothing in the feed pipeline (paging, sort, search, visibility)
 * has to learn about a second entity type.
 *
 * The mirror carries `newsId`, which is the flag — a post with one is a news
 * post, and the id is also what the card taps through to. `newsTitle` is kept
 * alongside so the banner can show the headline without fetching the article.
 *
 * These helpers are pure and live apart from the service that writes the post so
 * the shape of a mirror is testable and stated in one place.
 */

/** True when a post is the feed mirror of a news article. */
export const isNewsPost = (
  post?: Pick<Post, "newsId"> | null,
): boolean => Boolean(post?.newsId);

/** Headline first, then a formatted (bold/italic/link) lead-in — see `htmlToFormattedExcerpt`. */
export const buildNewsPostContent = (
  title?: string | null,
  content?: string | null,
): string => {
  const titleText = (title ?? "").trim();
  const headline = titleText ? `**${titleText}**` : "";
  const excerpt = htmlToFormattedExcerpt(content);
  return [headline, excerpt].filter(Boolean).join("\n\n");
};

/**
 * The cover image leads, then the article's own attachments.
 *
 * A post stores attachments as bare URL strings while news stores objects, so
 * the shape has to be flattened here. Order matters: the card renders the first
 * media entry as the hero image, and for an article that is the cover.
 */
export const buildNewsPostAttachmentUrls = (
  coverImageUrl?: string | null,
  attachments?: readonly { url?: string | null }[] | null,
): string[] =>
  [
    coverImageUrl ?? "",
    ...(attachments ?? []).map((attachment) => attachment?.url ?? ""),
  ].filter((url): url is string => Boolean(url));

/**
 * News visibility ("Public" / "Group") expressed as post visibility
 * ("public" / "group"), which is what `filterPostVisibility` in use-posts reads.
 *
 * A group article becomes a group post targeted through `visibleToGroupIds`
 * rather than `groupId`: it was not composed inside a single group, and
 * `visibleToGroupIds` is the field that carries an audience of several.
 */
export const buildNewsPostAudience = (
  newsVisibility?: string | null,
  groupIds?: readonly string[] | null,
): { visibility: "public" | "group"; visibleToGroupIds: string[] } =>
  newsVisibility === "Group"
    ? { visibility: "group", visibleToGroupIds: [...(groupIds ?? [])] }
    : { visibility: "public", visibleToGroupIds: [] };
